import type {
  CadCommand,
  ViewerRestoreProjectStateInDocumentRuntimeSwitchInput,
} from '@puppycad/cad-core';

/**
 * Preview 页面需要的 Viewer 文档恢复能力。
 */
export interface PublishedPreviewRuntimeBoundaryViewer {
  /**
   * 在完整文档 runtime switch 边界中恢复项目状态。
   *
   * @param input 文档恢复输入。
   * @returns 恢复完成后的异步结果。
   */
  restoreProjectStateInDocumentRuntimeSwitch(
    input: ViewerRestoreProjectStateInDocumentRuntimeSwitchInput,
  ): Promise<unknown>;
}

/**
 * 已发布预览模型恢复输入。
 */
export interface RestorePublishedPreviewModelInput {
  /** 当前 Viewer 实例。 */
  readonly viewer: PublishedPreviewRuntimeBoundaryViewer | null | undefined;
  /** 当前预览项目 ID。 */
  readonly projectId: string;
  /** 已发布版本号；缺省时使用 published 作为 runtimeKey 片段。 */
  readonly publishedVersion?: number;
  /** 已发布版本对应的命令历史，允许为空以清空旧 Worker runtime。 */
  readonly commands: readonly CadCommand[];
}

/**
 * 创建已发布预览页面的完整文档 runtimeKey。
 *
 * @param projectId 项目 ID。
 * @param publishedVersion 已发布版本号。
 * @returns 文档 runtimeKey。
 */
export function createPublishedPreviewRuntimeKey(
  projectId: string,
  publishedVersion: number | undefined,
): string {
  return `preview:${projectId}:${publishedVersion ?? 'published'}`;
}

/**
 * 在 Viewer 文档级 runtime 边界中恢复已发布预览模型。
 *
 * 设计说明：
 * 1. Preview 首屏恢复是完整 CAD 文档替换，必须通过 cad-core 深入口让 Worker 先写 staged runtime；
 * 2. 空命令同样代表“已发布为空模型”，也必须触发 full-document commit 来清空旧 active Worker；
 * 3. helper 只组织 runtimeKey 和调用参数，不在 web-client 拼接 begin/replay/commit 顺序。
 *
 * @param input 已发布预览模型恢复输入。
 */
export async function restorePublishedPreviewModel(
  input: RestorePublishedPreviewModelInput,
): Promise<void> {
  if (!input.viewer) {
    throw new Error('Preview 恢复模型缺少 Viewer 实例');
  }

  const runtimeKey = createPublishedPreviewRuntimeKey(input.projectId, input.publishedVersion);
  await input.viewer.restoreProjectStateInDocumentRuntimeSwitch({
    runtimeKey,
    reason: 'project-context-switch',
    scope: {
      kind: 'full-document',
      runtimeKey,
    },
    commands: [...input.commands],
  });
}
